import { ApolloLink, Observable, FetchResult } from '@apollo/client';
import type { AnalysisType } from 'Generated/schema';
import {
  DEV_WORKBENCH_FIXTURE_ID,
  DEV_WORKBENCH_FIXTURE_INSTALLED_ITEM,
  DEV_WORKBENCH_FIXTURE_PAYLOAD,
  DEV_WORKBENCH_FIXTURE_STATUS,
  DEV_WORKBENCH_PICKER_ITEMS,
  filterDevWorkbenchPickerItems,
  isDevWorkbenchFixtureActive,
} from 'Pages/Configure/ContentCatalog/Workbench/devFixtures/conflictedItemFixture';

/**
 * Dev-only Apollo link. When `isDevWorkbenchFixtureActive()` returns true it:
 *
 *   - Short-circuits `LoadAnalysisItemForWorkbench` for the fixture id and returns the canned
 *     YAML+Python payload (so the editor renders without round-tripping to the real backend).
 *   - Prepends the fixture row to real `ListInstalledCatalogItemUpdateStatuses` and
 *     `ListInstalledCatalogItems` responses so the workbench surfaces it in multi-item mode.
 *   - Short-circuits `ListAnalysisItemsForWorkbench` entirely with the picker fixture list,
 *     applying the same `analysisTypes` / `nameOrDescContains` filtering the real backend will.
 *     The real resolver isn't implemented yet (EPD-5843); the fixture lets the Add Files mode
 *     UI iterate against a realistic shape until the backend lands.
 *
 * Everything else is forwarded unchanged. The whole module is gated behind
 * `process.env.NODE_ENV === 'development'`, so it's tree-shaken out of production bundles.
 */

type AnyData = Record<string, unknown>;

const cloneInstalledItemsWithFixture = (data: AnyData | null | undefined): AnyData => {
  const list =
    ((data?.installedCatalogItems as AnyData | undefined)?.items as AnyData[] | undefined) ?? [];
  const alreadyPresent = list.some(item => item?.id === DEV_WORKBENCH_FIXTURE_ID);
  if (alreadyPresent) {
    return (data as AnyData) ?? {};
  }
  return {
    ...(data ?? {}),
    installedCatalogItems: {
      ...((data?.installedCatalogItems as AnyData) ?? {
        __typename: 'ListInstalledCatalogItemsOutput',
      }),
      items: [DEV_WORKBENCH_FIXTURE_INSTALLED_ITEM, ...list],
    },
  };
};

const cloneStatusesWithFixture = (data: AnyData | null | undefined): AnyData => {
  const list =
    ((data?.installedCatalogItemUpdateStatuses as AnyData | undefined)?.items as
      | AnyData[]
      | undefined) ?? [];
  const alreadyPresent = list.some(item => item?.id === DEV_WORKBENCH_FIXTURE_ID);
  if (alreadyPresent) {
    return (data as AnyData) ?? {};
  }
  return {
    ...(data ?? {}),
    installedCatalogItemUpdateStatuses: {
      ...((data?.installedCatalogItemUpdateStatuses as AnyData) ?? {
        __typename: 'ListInstalledCatalogItemUpdateStatusesOutput',
      }),
      items: [DEV_WORKBENCH_FIXTURE_STATUS, ...list],
    },
  };
};

const devWorkbenchSeedLink =
  process.env.NODE_ENV === 'development'
    ? new ApolloLink((operation, forward) => {
        if (!isDevWorkbenchFixtureActive()) {
          return forward(operation);
        }

        const opName = operation.operationName;

        if (opName === 'LoadAnalysisItemForWorkbench') {
          const targetId = (
            (operation.variables as AnyData | undefined)?.input as AnyData | undefined
          )?.id;
          if (targetId === DEV_WORKBENCH_FIXTURE_ID) {
            return new Observable<FetchResult>(observer => {
              observer.next({
                data: { loadAnalysisItemForWorkbench: DEV_WORKBENCH_FIXTURE_PAYLOAD },
              });
              observer.complete();
            });
          }
          return forward(operation);
        }

        if (opName === 'ListAnalysisItemsForWorkbench') {
          // Backend resolver doesn't exist yet (EPD-5843), so short-circuit unconditionally
          // when the dev flag is on. The real query input shape is mirrored so the picker UI
          // exercises the same filter semantics it'll use against the live backend.
          const input = (operation.variables as AnyData | undefined)?.input as AnyData | undefined;
          const analysisTypes = input?.analysisTypes as AnalysisType[] | null | undefined;
          const nameOrDescContains = input?.nameOrDescContains as string | null | undefined;
          const items = filterDevWorkbenchPickerItems(DEV_WORKBENCH_PICKER_ITEMS, {
            analysisTypes,
            nameOrDescContains,
          });
          return new Observable<FetchResult>(observer => {
            observer.next({
              data: {
                listAnalysisItemsForWorkbench: {
                  __typename: 'ListAnalysisItemsForWorkbenchOutput',
                  items,
                },
              },
            });
            observer.complete();
          });
        }

        if (
          opName === 'ListInstalledCatalogItems' ||
          opName === 'ListInstalledCatalogItemUpdateStatuses'
        ) {
          return new Observable<FetchResult>(observer => {
            const subscription = forward(operation).subscribe({
              next: result => {
                const augmented =
                  opName === 'ListInstalledCatalogItems'
                    ? cloneInstalledItemsWithFixture(result.data as AnyData | null | undefined)
                    : cloneStatusesWithFixture(result.data as AnyData | null | undefined);
                observer.next({ ...result, data: augmented });
              },
              error: err => observer.error(err),
              complete: () => observer.complete(),
            });
            return () => subscription.unsubscribe();
          });
        }

        return forward(operation);
      })
    : null;

export default devWorkbenchSeedLink;
